// Reglas de la etapa Contratante del wizard (specs/007-wizard-etapa-contratante, FR-002 y FR-008).
// Funciones puras: se prueban sin montar componentes.

// Persona física: 4 letras, fecha AAMMDD y homoclave de 3 caracteres (13 en total).
const RFC_PERSONA_FISICA = /^[A-ZÑ&]{4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/;

// Tipos de medio de contacto que el formulario fija y son obligatorios.
export const TIPOS_MEDIO_BASE = [2, 4, 5];

const TIPO_DOMICILIO_PERSONAL = 1;
const TIPO_DOMICILIO_FISCAL = 3;

export function normalizarRfc(rfc) {
  return String(rfc ?? '').trim().toUpperCase();
}

export function esRfcPersonaFisica(rfc) {
  return RFC_PERSONA_FISICA.test(normalizarRfc(rfc));
}

// Mensaje para un RFC que no se puede buscar; `null` si es válido.
export function mensajeRfc(rfc) {
  const normalizado = normalizarRfc(rfc);
  if (!normalizado) return 'Ingrese el RFC del contratante.';
  if (normalizado.length === 12) return 'El RFC debe ser de persona física (13 caracteres).';
  if (!esRfcPersonaFisica(normalizado)) return 'Ingrese un RFC válido de persona física (13 caracteres).';
  return null;
}

const vacio = (valor) => valor === null || valor === undefined || String(valor).trim() === '';

function faltantesDomicilio(domicilio, etiqueta) {
  if (!domicilio) return [`${etiqueta}: capture el domicilio.`];
  const faltan = [];
  // El identificador solo existe si el código postal se buscó con la lupa y se encontró.
  if (vacio(domicilio.codigoPostalId)) faltan.push('código postal válido (búsquelo con la lupa)');
  if (vacio(domicilio.calle)) faltan.push('calle');
  if (vacio(domicilio.numeroExt)) faltan.push('número exterior');
  return faltan.length ? [`${etiqueta}: falta ${faltan.join(', ')}.`] : [];
}

// Devuelve la lista (en español) de lo que falta o está mal para poder avanzar; vacía si todo está completo.
// Obligatorios: datos generales (el apellido materno es opcional), domicilio personal y medios base.
// Opcionales: cuentas financieras y domicilio fiscal igual al personal.
// `nombreTipoMedio(tipo)` permite nombrar el medio con el catálogo en el mensaje.
export function validarContratante(datos, { nombreTipoMedio = (tipo) => `tipo ${tipo}` } = {}) {
  const errores = [];
  const mensajeDelRfc = mensajeRfc(datos.rfc);
  if (mensajeDelRfc) errores.push(mensajeDelRfc);

  if (vacio(datos.curp)) errores.push('Falta la CURP.');
  if (vacio(datos.fechaNacimiento)) errores.push('Falta la fecha de nacimiento.');
  if (vacio(datos.nombre)) errores.push('Falta el nombre.');
  if (vacio(datos.paterno)) errores.push('Falta el apellido paterno.');

  const domicilios = Array.isArray(datos.domicilios) ? datos.domicilios : [];
  errores.push(...faltantesDomicilio(
    domicilios.find((d) => Number(d.tipoId) === TIPO_DOMICILIO_PERSONAL),
    'Domicilio personal',
  ));
  if (datos.domicilioFiscalIgual === false) {
    errores.push(...faltantesDomicilio(
      domicilios.find((d) => Number(d.tipoId) === TIPO_DOMICILIO_FISCAL),
      'Domicilio fiscal',
    ));
  }

  const medios = Array.isArray(datos.mediosContacto) ? datos.mediosContacto : [];
  TIPOS_MEDIO_BASE.forEach((tipo) => {
    const medio = medios.find((m) => Number(m.tipoMedioId) === tipo);
    if (!medio || vacio(medio.descripcion)) {
      errores.push(`Falta el medio de contacto obligatorio (${nombreTipoMedio(tipo)}).`);
    }
  });

  const cuentas = Array.isArray(datos.informacionFinanciera) ? datos.informacionFinanciera : [];
  cuentas.forEach((cuenta, i) => {
    if (vacio(cuenta.instrumentoId) || vacio(cuenta.bancoId) || vacio(cuenta.cuenta)) {
      errores.push(`Cuenta bancaria ${i + 1}: capture instrumento, banco y número de cuenta, o quítela.`);
    }
  });

  return errores;
}
